import React from "react";
import { Trophy, Clock, Sparkles, TrendingUp, Activity } from "lucide-react";
import { ProgressRing } from "../ProgressRing";
import { insightsPanel } from "../../data/marketplaceData";

const Section = ({ title, icon: Icon, action, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white cf-shadow p-5">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={14} className="text-slate-500" />}
        <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">{title}</p>
      </div>
      {action && <button className="text-[11.5px] font-semibold text-indigo-700 hover:underline">{action}</button>}
    </div>
    {children}
  </div>
);

export const MarketplaceInsightsPanel = () => {
  return (
    <aside
      className="hidden xl:flex fixed right-0 top-0 w-[360px] h-screen flex-col border-l cf-divider-soft z-30 cf-scroll overflow-y-auto" style={{ background: "var(--cf-app-bg)" }}
      data-testid="marketplace-insights-panel"
    >
      <div className="px-6 pt-6 pb-2 sticky top-0 z-10">
        <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Network performance</p>
        <h3 className="font-display text-[20px] font-bold text-slate-900 mt-1">Revenue intelligence</h3>
      </div>

      <div className="px-6 pb-8 space-y-4">
        {/* Revenue ring */}
        <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-5 cf-shadow" data-testid="revenue-ring-card">
          <div className="flex items-center gap-2 text-indigo-300">
            <TrendingUp size={14} />
            <p className="text-[10.5px] uppercase tracking-[0.22em] font-semibold">Monthly target progress</p>
          </div>
          <div className="mt-3 flex items-end gap-4">
            <ProgressRing value={insightsPanel.revenueRing} size={68} stroke={6} color="#6366F1" label={`${insightsPanel.revenueRing}%`} />
            <div>
              <p className="font-display text-[20px] font-bold leading-none">£128.4K</p>
              <p className="text-[12px] text-slate-400 mt-1">of {insightsPanel.revenueTarget} target</p>
            </div>
          </div>
          <p className="text-[11.5px] text-indigo-300 font-semibold mt-3">+18.2% MoM · pacing 4 days ahead</p>
        </div>

        {/* Leaderboard */}
        <Section title="Partner leaderboard" icon={Trophy} action="View all">
          <ul className="space-y-2.5">
            {insightsPanel.leaderboard.map((p, i) => (
              <li key={p.name} data-testid={`leader-${i}`} className="flex items-center gap-3">
                <span className="font-mono-cf text-[11px] text-slate-400 w-4">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-[12.5px] font-medium text-slate-900 truncate">{p.name}</p>
                  <p className="text-[11px] text-slate-500">{p.deals} deals</p>
                </div>
                <div className="text-right">
                  <p className="font-display text-[13px] font-bold text-slate-900 leading-none">{p.revenue}</p>
                  <p className="text-[10px] text-indigo-600 font-semibold mt-0.5">{p.change}</p>
                </div>
              </li>
            ))}
          </ul>
        </Section>

        {/* Fastest solicitors */}
        <Section title="Fastest completing solicitors" icon={Clock}>
          <ul className="space-y-2.5">
            {insightsPanel.fastestSolicitors.map((s, i) => (
              <li key={s.name} data-testid={`fast-${i}`} className="flex items-center justify-between">
                <p className="text-[12.5px] font-medium text-slate-900">{s.name}</p>
                <span className="font-display text-[14px] font-bold text-indigo-700">{s.days}d</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Best brokers */}
        <Section title="Best-performing brokers" icon={TrendingUp}>
          <ul className="space-y-3">
            {insightsPanel.bestBrokers.map((b, i) => (
              <li key={b.name} data-testid={`broker-${i}`}>
                <div className="flex items-center justify-between text-[11.5px] mb-1">
                  <span className="text-slate-700">{b.name}</span>
                  <span className="font-mono-cf text-slate-900 font-semibold">{b.conv}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-indigo-500" style={{ width: `${b.conv}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </Section>

        {/* Response rankings */}
        <Section title="Service response rankings" icon={Activity}>
          <ul className="space-y-2.5">
            {insightsPanel.responseRanks.map((r, i) => (
              <li key={r.name} data-testid={`response-${i}`} className="flex items-center justify-between">
                <p className="text-[12.5px] font-medium text-slate-900">{r.name}</p>
                <span className="text-[11px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-full">{r.hrs}h avg</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Growth opps */}
        <div className="rounded-2xl border border-indigo-200/80 bg-indigo-50/40 p-5" data-testid="growth-opps">
          <div className="flex items-center gap-2 text-indigo-700">
            <Sparkles size={14} />
            <p className="text-[10.5px] uppercase tracking-[0.22em] font-semibold">AI growth opportunities</p>
          </div>
          <ul className="mt-3 space-y-2.5">
            {insightsPanel.growthOpps.map((g, i) => (
              <li key={i} data-testid={`grow-${i}`} className="flex items-start justify-between gap-3">
                <p className="text-[12.5px] text-slate-700 leading-snug">{g.title}</p>
                <span className="text-[10.5px] font-semibold text-indigo-700 whitespace-nowrap">{g.impact}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </aside>
  );
};
